import { Plan } from '@wix/ambassador-pricing-plans-v3-plan/types';
import { uoUFunnelPlanCustomisationPageSrc92Evid115 } from '@wix/bi-logger-pricing-plans-data/v2';
import { PlanCustomizationPublicData } from '@wix/pricing-plans-router-utils';
import { allowsFutureStartDateV3 } from '@wix/pricing-plans-utils';
import type { CreateControllerFn, ControllerParams, ControllerFlowAPI, FlowAPI } from '@wix/yoshi-flow-editor';
import { DEMO_FORM_ID, plansFixtureTranslated } from '../../fixtures';
import { EcomClient } from '../../services/ecom-client';
import { resolveLocale } from '../../utils';
import { toError } from '../../utils/errors';
import { PlanCustomizationInteraction } from './fedops';
import { planV2ToDemoPlanV3 } from './mappers/plan-v2-to-demo-plan-v3';
import { FormsService } from './services/forms';
import { PlanCustomization } from './types';
import { RootProductPageProps } from './Widget';

const createController: CreateControllerFn = async ({ flowAPI }: ControllerParams) => {
  function reportInteractionStarted(name: PlanCustomizationInteraction) {
    flowAPI.panoramaClient.transaction(name).start();
  }

  function reportInteractionFinished(name: PlanCustomizationInteraction) {
    flowAPI.panoramaClient.transaction(name).finish();
  }

  return {
    async pageReady() {
      reportInteractionStarted(PlanCustomizationInteraction.Initialize);
      const wixCodeApi = flowAPI.controllerConfig.wixCodeApi;
      const setProps: (props: Partial<RootProductPageProps>) => void = flowAPI.controllerConfig.setProps;
      const formsService = new FormsService(flowAPI);

      setProps({
        status: 'PAGE_LOADING',
        formControllerStatus: 'LOADING',
        locale: resolveLocale(wixCodeApi),
        proceedToPayment,
        clearStatusMessage: () => setProps({ statusMessage: undefined }),

        // @ts-expect-error Undocumented viewer prop, which adds "height: auto" to root TPA section
        fitToContentHeight: true,
      });

      reportInteractionStarted(PlanCustomizationInteraction.GetProductPageData);
      const routerData = await getProductPageData(flowAPI);
      reportInteractionFinished(PlanCustomizationInteraction.GetProductPageData);

      logCustomizationPageEvent({
        actionGroup: 'load',
        actionName: 'start_load',
      });
      const {
        plan,
        customizationData: { integrationData, checkoutFlowId },
      } = routerData;
      const ecomClient = new EcomClient({
        wixCodeApi,
        flowAPI,
        widgetType: 'customization',
      });

      reportInteractionStarted(PlanCustomizationInteraction.InitializeFormsController);
      formsService
        .maybeInitFormController(plan)
        .then(() => {
          reportInteractionFinished(PlanCustomizationInteraction.InitializeFormsController);
          setProps({ formControllerStatus: 'READY' });
        })
        .catch(() => {
          setProps({ formControllerStatus: 'ERROR' });
        });

      setProps({
        status: 'PAGE_READY',
        plan,
        minStartDate: integrationData.minStartDate ? integrationData.minStartDate : undefined,
        maxStartDate: integrationData.maxStartDate ? integrationData.maxStartDate : undefined,
      });
      logCustomizationPageEvent({
        actionGroup: 'load',
        actionName: 'finish_load',
      });

      reportInteractionFinished(PlanCustomizationInteraction.Initialize);

      async function proceedToPayment({ formValues, startDate }: PlanCustomization) {
        reportInteractionStarted(PlanCustomizationInteraction.ProceedToPayment);
        logCustomizationPageEvent({
          actionGroup: 'click',
          actionName: 'continue',
        });
        setProps({ status: 'CHECKOUT_LOADING', statusMessage: undefined });
        const submissionId = await formsService.maybeSubmitForm(plan.formId, formValues).catch((error) => {
          flowAPI.panoramaClient
            .errorMonitor()
            .reportError(toError(error), { tags: { interactionTag: 'proceed_to_payment' } });
          return null;
        });

        try {
          const validStartDate = isStartDateValid(startDate) ? startDate : undefined;
          await ecomClient.createAndNavigateToCheckout({
            plan,
            submissionId,
            startDate: validStartDate,
            integrationData,
            checkoutFlowId,
          });
          reportInteractionFinished(PlanCustomizationInteraction.ProceedToPayment);
        } catch (error) {
          const resolvedError = flowAPI.errorHandler.getResolvedError(error);
          setProps({ status: 'ERROR', statusMessage: resolvedError.message });
          logCustomizationPageEvent({
            actionGroup: 'error',
            actionName: resolvedError.applicationError?.code ?? '',
          });
        }
      }

      function logCustomizationPageEvent(params: { actionGroup: 'load' | 'click' | 'error'; actionName: string }) {
        if (!routerData) {
          return flowAPI.bi?.report(
            uoUFunnelPlanCustomisationPageSrc92Evid115({
              ...params,
            }),
          );
        }

        return flowAPI.bi?.report(
          uoUFunnelPlanCustomisationPageSrc92Evid115({
            isCustomStartDateEnabled: Boolean(allowsFutureStartDateV3(routerData.plan)).toString(),
            isCustomCheckoutFormEnabled: Boolean(routerData.plan.formId).toString(),
            checkoutFlowId: routerData.customizationData.checkoutFlowId,
            referralinfo: routerData.customizationData.integrationData.biOptions?.referralInfo,
            planGuid: routerData.plan.id!,
            ...params,
          }),
        );
      }
    },
    async updateConfig() {},
  };
};

export default createController;

async function getProductPageData(flowAPI: ControllerFlowAPI): Promise<PlanCustomizationPublicData> {
  const routerData = flowAPI.controllerConfig.wixCodeApi.window.getRouterPublicData<PlanCustomizationPublicData>();
  if (!routerData) {
    if (flowAPI.environment.isEditor || flowAPI.environment.isPreview) {
      const demoPlan = getDemoPlanV3(flowAPI.translations.t);
      return {
        plan: {
          ...demoPlan,
          currency: flowAPI.controllerConfig.wixCodeApi.site.currency,
        },
        customizationData: {
          integrationData: {},
          planId: demoPlan.id!,
          checkoutFlowId: undefined,
        },
      };
    }

    // TODO: Add fallback client calls to get the data
    throw new Error('Router data is missing');
  }

  return routerData;
}

function getDemoPlanV3(t: FlowAPI['translations']['t']): Plan {
  const [planV2] = plansFixtureTranslated(t);
  return planV2ToDemoPlanV3({
    ...planV2,
    formId: DEMO_FORM_ID,
    allowFutureStartDate: true,
  });
}

function isStartDateValid(dateString: string | null): boolean {
  if (!dateString) {
    return false;
  }

  const date = new Date(dateString);
  const today = new Date();
  if (date < today) {
    return false;
  }

  if (date.getDate() === today.getDate()) {
    return false;
  }

  return true;
}
